/* iceberg-ui */
import {
    Badge,
    Button,
    Flex,
    IconWithVariant
} from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Typography
} from '@mui/material';

/*  Shared  */
import { RunReportLink } from 'application/Shared/Components/Reports/RunReportLink';
import { FontSizes } from 'application/Shared/Constants';
import { RECIPE_ACTIONS } from 'application/Shared/Constants/Workato';
import { Route } from 'application/Shared/Helpers';
import { User as UserType } from 'application/Shared/types/user';

/*  Objects  */
import { User as UserContext, useUserContext } from 'objects/User';

/*  React  */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

export type OnboardOffboardActions = Extract<
    RECIPE_ACTIONS,
    RECIPE_ACTIONS.OFFBOARD | RECIPE_ACTIONS.ONBOARD
>;

const OnOffboardAction = ({ action }: { action: OnboardOffboardActions }) => {
    const { t } = useTranslation();
    const { applicationId, clientId, partnerId, userId } = useParams();
    const navigate = useNavigate();
    const userContext = useUserContext();
    const { user } = userContext || ({} as { user: UserType });

    const baseAdminRoute = Route.buildBaseAdminRoute({
        clientId: clientId!,
        partnerId
    });

    const page = `${baseAdminRoute}/saasOps/${action}`;
    const fromPage = userId ? 'user' : 'app';

    const searchParamsToSend = () => {
        if (userId) {
            return new URLSearchParams({
                email: user?.email || '',
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                name: `${user?.firstName} ${user?.lastName}`,
                userId
            }).toString();
        }
        if (applicationId)
            return new URLSearchParams({ applicationId }).toString();
        return '';
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            data-testid={`${action}_${fromPage}_actionsCard`}
        >
            <Box flexGrow="1" marginRight="8px">
                <Flex sx={{ gap: '8px' }}>
                    <Typography
                        fontWeight={600}
                        marginBottom="4px"
                        color="textSecondary"
                    >
                        {t(`Common.${action}`)}
                    </Typography>
                    <Badge variant="primary" text={t('Common.new')} />
                </Flex>
                <Typography>
                    {t(`SaasOps.${action}.user.appOverview`)}
                </Typography>
            </Box>
            <Button
                size="small"
                onClick={() => {
                    navigate({
                        pathname: page,
                        search: searchParamsToSend()
                    });
                }}
                data-testid={`navigateTo_${action}_${fromPage}_Btn`}
                data-trackid={`${action}User-${fromPage}-overview`}
            >
                {t('Common.start')}
            </Button>
        </Box>
    );
};

export const RunOffboardReportAction = () => {
    const { t } = useTranslation();

    const { clientId, partnerId, userId } = useParams();

    const fromPage = userId ? 'user' : 'app';

    const baseReportsRoute = Route.buildBaseReportsRoute({
        clientId,
        partnerId
    });

    return (
        <Flex alignItems="center">
            <Flex
                sx={{
                    flexDirection: 'column',
                    flexGrow: '1',
                    marginRight: '8px'
                }}
            >
                <Typography
                    fontWeight={600}
                    marginBottom="4px"
                    color="textSecondary"
                >
                    {t('Common.exportOffboardReport')}
                </Typography>
                <Typography>
                    {t('Common.exportOffboardReport_description')}
                </Typography>
                <Flex
                    sx={{
                        alignItems: 'center',
                        flexDirection: 'row',
                        marginTop: '16px'
                    }}
                >
                    <IconWithVariant
                        variant="warningLight"
                        src="Knowledge"
                        size={FontSizes[2]}
                    />
                    <Typography variant="caption" style={{ marginLeft: '8px' }}>
                        <Trans
                            i18nKey="Common.redirect.exportReports"
                            components={{
                                Link: (
                                    <Link
                                        to={`${baseReportsRoute}/reportSelection`}
                                        className="link--secondary"
                                        style={{ fontWeight: 600 }}
                                    />
                                )
                            }}
                        />
                    </Typography>
                </Flex>
            </Flex>
            <RunReportLink
                userSelection={userId}
                period="daily"
                reportType="offboard"
                run
            >
                <Button
                    size="small"
                    data-testid={`runOffboardReportButton_${fromPage}`}
                    data-trackid={`userActionsCardRunOffboardReportBtn_${fromPage}`}
                >
                    {t('Common.run')}
                </Button>
            </RunReportLink>
        </Flex>
    );
};

export const UserActionsCard = ({
    isOffboardAvailable,
    isOnboardAvailable
}: {
    isOffboardAvailable?: boolean;
    isOnboardAvailable?: boolean;
}) => {
    const { t } = useTranslation();
    const { applicationId, userId } = useParams();

    return (
        <Card
            variant="outlined"
            style={{ height: '100%', minHeight: '256px' }}
            data-testid="userActionsCard"
        >
            <Box display="flex" justifyContent="space-between">
                <CardHeader title={t('Users.actions')} />
            </Box>
            <CardContent>
                <UserContext id={userId}>
                    <Box>
                        {/* If we are in the app page, we only show workato actions that are available for that app */}
                        {((isOnboardAvailable && !!applicationId) ||
                            !!userId) && (
                            <>
                                <OnOffboardAction
                                    action={RECIPE_ACTIONS.ONBOARD}
                                />
                                <Divider
                                    sx={{
                                        borderColor: 'border.primary',
                                        marginBottom: '24px',
                                        marginTop: '24px'
                                    }}
                                />
                            </>
                        )}
                        {((isOffboardAvailable && !!applicationId) ||
                            !!userId) && (
                            <>
                                <OnOffboardAction
                                    action={RECIPE_ACTIONS.OFFBOARD}
                                />
                                <Divider
                                    sx={{
                                        borderColor: 'border.primary',
                                        marginBottom: '24px',
                                        marginTop: '24px'
                                    }}
                                />
                            </>
                        )}
                        <RunOffboardReportAction />
                    </Box>
                </UserContext>
            </CardContent>
        </Card>
    );
};
